import React from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { Alert, Box, CircularProgress, Grid } from '@mui/material';
import { loadImportedDataset } from '../../support/dataImport/persistWidgets';
import { WidgetSpec } from '../../support/dataImport/presets/types';
import WidgetRenderer from './WidgetRenderer';

/**
 * Read-only render of a saved import's widgets, by dataset id.
 *
 * Used wherever a `data_import` answer needs to be shown back — the post-submit assessment report and
 * the admin response-set detail. It reloads the `ImportedDataset` and re-renders its stored widgets
 * (pre-aggregated data + topology payloads), so it never re-parses the file or re-runs a preset.
 */

type Props = {
  datasetId: number;
  /** Optionally render only these widget ids (in order); defaults to all stored widgets. */
  widgetIds?: string[];
};

const ImportedWidgetsView: React.FC<Props> = ({ datasetId, widgetIds }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['imported-dataset', String(datasetId)],
    queryFn: () => loadImportedDataset(datasetId),
    ...{ enabled: !!datasetId, staleTime: 5 * 60 * 1000 },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (isError || !data) {
    return <Alert severity="warning">Imported dataset #{datasetId} could not be loaded.</Alert>;
  }

  const allWidgets: WidgetSpec[] = Array.isArray(data.widgets) ? data.widgets : [];
  const widgets = widgetIds?.length
    ? (widgetIds.map((id) => allWidgets.find((w) => w.id === id)).filter(Boolean) as WidgetSpec[])
    : allWidgets;

  if (widgets.length === 0) {
    return <Alert severity="info">This import has no widgets to display.</Alert>;
  }

  return (
    <Grid container spacing={2}>
      {widgets.map((spec, index) => (
        <Grid
          item
          xs={12}
          md={spec.chartType === 'topology' || spec.chartType === 'datagrid' ? 12 : 6}
          key={spec.id}
        >
          <WidgetRenderer spec={spec} index={index} height={spec.chartType === 'topology' ? 420 : 300} />
        </Grid>
      ))}
    </Grid>
  );
};

export default ImportedWidgetsView;
